import React, { useState, useEffect } from "react";
import { Loader, Table, Icon } from "semantic-ui-react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  Label,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { filter, map, orderBy } from "lodash";
import moment from "moment";
import { HeaderPage, PrintHeader, PrintTable } from "../../components/Admin";
import { useOrder, usePrintOrientation, usePrinting } from "../../hooks";
import { buildProductSalesReport, QUADRANTS } from "../../utils/productSales";
import { buildExecutiveSummary } from "../../utils/salesInsights";
import { getBusinessToday, getBusinessDaysAgo } from "../../utils/date";


export function ProductSalesReport() {
  const [dateFrom, setDateFrom] = useState(() => getBusinessDaysAgo(30));
  const [dateTo, setDateTo] = useState(getBusinessToday);
  const { loading, orders, getOrdersInRange } = useOrder();
  usePrintOrientation("portrait");

  useEffect(() => {
    if (dateFrom && dateTo) getOrdersInRange(dateFrom, dateTo);
  }, [dateFrom, dateTo]);

  const report = !loading && orders ? buildProductSalesReport(orders) : null;
  const summary = report ? buildExecutiveSummary(report) : null;
  const dateRangeLabel = `Del ${moment(dateFrom).format("DD/MM/YYYY")} al ${moment(
    dateTo
  ).format("DD/MM/YYYY")}`;

  return (
    <>
      <div className="screen-only">
        <HeaderPage
          title="Ventas por Producto"
          btnTitle={
            <>
              <Icon name="print" /> Imprimir
            </>
          }
          btnClick={() => window.print()}
        />
      </div>

      <div
        className="screen-only"
        style={{ display: "flex", gap: "15px", margin: "10px 0 20px" }}
      >
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Desde</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Hasta</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <PrintTable>
        <PrintHeader title="Ventas por Producto" subtitle={dateRangeLabel} />

        {loading ? (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        ) : report.totalQuantity === 0 ? (
          <p>No hay pedidos en el rango de fechas seleccionado.</p>
        ) : (
          <div className="print-content">
            <ExecutiveSummary summary={summary} />
            <QuadrantLegend />

            {map(orderBy(report.categories, "revenue", "desc"), (category) => (
              <CategoryReport key={category.title} category={category} />
            ))}
          </div>
        )}
      </PrintTable>
    </>
  );
}

function ExecutiveSummary(props) {
  const { summary } = props;

  return (
    <div
      style={{
        background: "#fff8f0",
        border: "1px solid #e67e22",
        borderRadius: 8,
        padding: "16px 20px",
        marginBottom: 25,
      }}
    >
      <h3 style={{ marginTop: 0 }}>Resumen ejecutivo</h3>

      <strong>Conclusiones clave</strong>
      <ul>
        {map(summary.conclusions, (text, index) => (
          <li key={index}>{text}</li>
        ))}
      </ul>

      <strong>Acciones sugeridas</strong>
      <ul>
        {map(summary.actions, (text, index) => (
          <li key={index}>{text}</li>
        ))}
      </ul>
    </div>
  );
}

function QuadrantLegend() {
  return (
    <div style={{ display: "flex", gap: "18px", flexWrap: "wrap", marginBottom: 25 }}>
      {map(QUADRANTS, (quadrant) => (
        <div key={quadrant.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: quadrant.color,
              display: "inline-block",
            }}
          />
          <span style={{ fontSize: "0.85rem" }}>
            <strong>{quadrant.label}</strong> ({quadrant.description})
          </span>
        </div>
      ))}
    </div>
  );
}

const LOW_DATA_MIN_PRODUCTS = 2;
const LOW_DATA_MIN_SALES = 5;

function CategoryReport(props) {
  const { category } = props;
  const withCost = filter(category.products, "hasCost");
  const withoutCost = filter(category.products, (p) => !p.hasCost);
  const sortedProducts = orderBy(category.products, "quantity", "desc");
  const lowData =
    withCost.length < LOW_DATA_MIN_PRODUCTS || category.quantity < LOW_DATA_MIN_SALES;

  return (
    <div style={{ marginBottom: 45, breakInside: "avoid" }}>
      <h4 style={{ marginBottom: 4 }}>{category.title}</h4>
      <p style={{ color: "#666", marginTop: 0 }}>
        {category.quantity} unidades vendidas — S/ {category.revenue.toFixed(2)} en ingresos
      </p>

      {withCost.length > 0 && lowData && (
        <div
          style={{
            background: "#fffaf0",
            border: "1px solid #f2c037",
            borderRadius: 4,
            padding: "8px 12px",
            marginBottom: 12,
            fontSize: "0.85rem",
            color: "#8a6416",
          }}
        >
          ⚠ Pocos datos en este período ({withCost.length} producto(s) clasificado(s), {category.quantity}{" "}
          unidad(es) vendida(s)) — el cuadrante puede no ser representativo.
        </div>
      )}

      {withCost.length > 0 && (
        <QuadrantChart category={category} products={withCost} />
      )}

      {withoutCost.length > 0 && (
        <p style={{ color: "#999", fontSize: "0.85rem" }}>
          Sin costo estimado (no se clasifican en el gráfico): {map(withoutCost, "title").join(", ")}
        </p>
      )}

      <Table compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Producto</Table.HeaderCell>
            <Table.HeaderCell>Unidades</Table.HeaderCell>
            <Table.HeaderCell>Ingresos</Table.HeaderCell>
            <Table.HeaderCell>Margen</Table.HeaderCell>
            <Table.HeaderCell>Cuadrante</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {map(sortedProducts, (product) => (
            <Table.Row key={product.id}>
              <Table.Cell>{product.title}</Table.Cell>
              <Table.Cell>{product.quantity}</Table.Cell>
              <Table.Cell>S/ {product.revenue.toFixed(2)}</Table.Cell>
              <Table.Cell>
                {product.hasCost ? `S/ ${product.margin.toFixed(2)}` : "—"}
              </Table.Cell>
              <Table.Cell>
                {product.quadrant ? (
                  <span style={{ color: product.quadrant.color, fontWeight: 700 }}>
                    {product.quadrant.label}
                  </span>
                ) : (
                  "—"
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

const AREA_POSITIONS = {
  ESTRELLA: "insideTopRight",
  DILEMA: "insideTopLeft",
  CABALLO: "insideBottomRight",
  PERRO: "insideBottomLeft",
};

// Ancho del gráfico al imprimir (hoja vertical) — ver usePrinting.
const PRINT_CHART_WIDTH = 660;

function QuadrantChart(props) {
  const { category, products } = props;
  const printing = usePrinting();
  const { avgQuantity, avgMargin } = category;

  const quantities = map(products, "quantity");
  const margins = map(products, "margin");
  const maxQuantity = Math.max(...quantities);
  const minMargin = Math.min(...margins);
  const maxMargin = Math.max(...margins);

  const xMax = maxQuantity + Math.max(1, maxQuantity * 0.15);
  const marginRange = maxMargin - minMargin || 10;
  const yPadding = marginRange * 0.15 || 5;
  const yMin = minMargin - yPadding;
  const yMax = maxMargin + yPadding;

  return (
    <ResponsiveContainer width={printing ? PRINT_CHART_WIDTH : "100%"} height={320}>
      <ScatterChart margin={{ top: 10, right: 30, bottom: 25, left: 20 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          type="number"
          dataKey="quantity"
          domain={[0, xMax]}
          allowDecimals={false}
        >
          <Label value="Unidades Vendidas" position="insideBottom" offset={-15} />
        </XAxis>
        <YAxis type="number" dataKey="margin" domain={[yMin, yMax]} allowDecimals={false}>
          <Label
            value="Margen de Ganancia (S/)"
            angle={-90}
            position="insideLeft"
            style={{ textAnchor: "middle" }}
          />
        </YAxis>
        <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<ProductTooltip />} />

        <ReferenceArea
          x1={0}
          x2={avgQuantity}
          y1={avgMargin}
          y2={yMax}
          fill={QUADRANTS.PUZZLE.color}
          fillOpacity={0.08}
        >
          <Label
            content={
              <QuadrantAreaLabel quadrant={QUADRANTS.PUZZLE} position={AREA_POSITIONS.DILEMA} />
            }
          />
        </ReferenceArea>
        <ReferenceArea
          x1={avgQuantity}
          x2={xMax}
          y1={avgMargin}
          y2={yMax}
          fill={QUADRANTS.ESTRELLA.color}
          fillOpacity={0.08}
        >
          <Label
            content={
              <QuadrantAreaLabel quadrant={QUADRANTS.ESTRELLA} position={AREA_POSITIONS.ESTRELLA} />
            }
          />
        </ReferenceArea>
        <ReferenceArea
          x1={0}
          x2={avgQuantity}
          y1={yMin}
          y2={avgMargin}
          fill={QUADRANTS.PERRO.color}
          fillOpacity={0.08}
        >
          <Label
            content={
              <QuadrantAreaLabel quadrant={QUADRANTS.PERRO} position={AREA_POSITIONS.PERRO} />
            }
          />
        </ReferenceArea>
        <ReferenceArea
          x1={avgQuantity}
          x2={xMax}
          y1={yMin}
          y2={avgMargin}
          fill="#fbbd08"
          fillOpacity={0.15}
        >
          <Label
            content={
              <QuadrantAreaLabel quadrant={QUADRANTS.CABALLO} position={AREA_POSITIONS.CABALLO} />
            }
          />
        </ReferenceArea>

        <ReferenceLine x={avgQuantity} stroke="#999" strokeDasharray="4 4" />
        <ReferenceLine y={avgMargin} stroke="#999" strokeDasharray="4 4" />

        <Scatter data={products} isAnimationActive={false}>
          {map(products, (product, index) => (
            <Cell
              key={index}
              fill={product.quadrant.color}
              fillOpacity={0.55}
              stroke={product.quadrant.color}
              strokeWidth={2}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
}

// Recharts pasa `viewBox` con la posicion en pixeles del ReferenceArea al usar `content`.
function QuadrantAreaLabel(props) {
  const { viewBox, quadrant, position } = props;
  if (!viewBox) return null;

  const { x, y, width, height } = viewBox;
  const isTop = position.includes("Top");
  const isLeft = position.includes("Left");

  const textX = isLeft ? x + 8 : x + width - 8;
  const textY = isTop ? y + 16 : y + height - 24;
  const anchor = isLeft ? "start" : "end";

  return (
    <g>
      <text x={textX} y={textY} textAnchor={anchor} fontSize={12} fontWeight={700} fill={quadrant.color}>
        {quadrant.label.toUpperCase()}
      </text>
      <text x={textX} y={textY + 14} textAnchor={anchor} fontSize={10} fill="#888">
        {quadrant.description}
      </text>
    </g>
  );
}

function ProductTooltip(props) {
  const { active, payload } = props;
  if (!active || !payload?.length) return null;

  const product = payload[0].payload;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ddd",
        borderRadius: 4,
        padding: "8px 12px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      }}
    >
      <strong>{product.title}</strong>
      <div>Unidades: {product.quantity}</div>
      <div>Margen: S/ {product.margin.toFixed(2)}</div>
    </div>
  );
}
